import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import styles from './Login.module.css';

interface LocationState {
  from?: string;
}

export default function Login(): JSX.Element {
  const { user, signInWithGoogle, isMockMode, authMode, authLoading, authError, retryAuth } = useAuth();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Fleet (dev) mode asks the backend who we are on load. Until that answers,
  // offering "Sign in" would send an already-signed-in user round the proxy.
  const checkingSession = authMode === 'fleet' && authLoading;

  if (user) {
    const redirectTo = (location.state as LocationState | null)?.from ?? '/games';
    return <Navigate to={redirectTo} replace />;
  }

  async function onSignIn(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.page}>
      <main className={styles.card} aria-labelledby="login-heading">
        <div className={styles.eyebrow}>Live MLB</div>
        <h1 id="login-heading" className={styles.title}>Ball Watcher</h1>
        <p className={styles.tagline}>Real-time and historical game view.</p>
        <button
          type="button"
          className={styles.button}
          onClick={onSignIn}
          disabled={busy || checkingSession}
          aria-busy={checkingSession || undefined}
        >
          <svg className={styles.googleIcon} viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
            />
          </svg>
          {checkingSession ? 'Checking your session…' : busy ? 'Signing in…' : 'Sign in with Google'}
        </button>
        <p className={styles.patrickComment}>whoa, Patrick is the fucking man!</p>
        {isMockMode && (
          <p className={styles.mockNote}>Mock auth — no real Google sign-in required.</p>
        )}
        {error && <p role="alert" className={styles.error}>{error}</p>}
        {authError && !checkingSession && (
          <>
            <p role="alert" className={styles.error} data-testid="auth-error">{authError.message}</p>
            <button type="button" className={styles.button} onClick={retryAuth} data-testid="auth-retry">
              Try again
            </button>
          </>
        )}
      </main>
    </div>
  );
}
